import { useCallback, useEffect, useMemo, useState } from 'react'
import { EmptyState } from '../components/EmptyState'
import { InlineNotice } from '../components/InlineNotice'
import { LoadingBlock } from '../components/LoadingBlock'
import { StatusBadge } from '../components/StatusBadge'
import { useLanguage } from '../contexts/LanguageContext'
import { clientDisplayName, formatDate, leadStatusLabel, leadStatusTone, serviceName } from '../lib/format'
import { supabase } from '../lib/supabase'
import type { LeadRow } from '../lib/types'

type DashboardData = {
  open: number
  today: number
  waiting: number
  wonMonth: number
  lostMonth: number
  conversion: number
  recent: LeadRow[]
}

const emptyData: DashboardData = { open: 0, today: 0, waiting: 0, wonMonth: 0, lostMonth: 0, conversion: 0, recent: [] }

function startOfTodayIso() {
  const date = new Date()
  date.setHours(0, 0, 0, 0)
  return date.toISOString()
}

function startOfMonthIso() {
  const date = new Date()
  date.setDate(1)
  date.setHours(0, 0, 0, 0)
  return date.toISOString()
}

export function DashboardPage({ onOpenLead }: { onOpenLead?: (id: string) => void }) {
  const { t, language } = useLanguage()
  const [data, setData] = useState<DashboardData>(emptyData)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!supabase) return
    setLoading(true)
    setError(null)
    const monthStart = startOfMonthIso()
    const todayStart = startOfTodayIso()
    const [openResult, todayResult, waitingResult, monthResult, decidedResult, recentResult] = await Promise.all([
      supabase.from('leads').select('id', { count: 'exact', head: true }).not('status', 'in', '(won,lost)'),
      supabase.from('leads').select('id', { count: 'exact', head: true }).gte('created_at', todayStart),
      supabase.from('leads').select('id', { count: 'exact', head: true }).eq('status', 'waiting_for_client'),
      supabase.from('leads').select('status').gte('updated_at', monthStart).in('status', ['won', 'lost']),
      supabase.from('leads').select('status').in('status', ['won', 'lost']),
      supabase.from('leads').select(`
        *,
        clients(id,client_type,first_name,last_name,company_name,instagram_handle,email,phone),
        services(id,code,name_pl,name_ru),
        responsible:profiles!leads_responsible_user_id_fkey(id,full_name,email,role)
      `).order('created_at', { ascending: false }).limit(6)
    ])
    const firstError = openResult.error || todayResult.error || waitingResult.error || monthResult.error || decidedResult.error || recentResult.error
    if (firstError) {
      setError(firstError.message)
    } else {
      const monthRows = monthResult.data || []
      const decidedRows = decidedResult.data || []
      const won = decidedRows.filter((row) => row.status === 'won').length
      const lost = decidedRows.filter((row) => row.status === 'lost').length
      setData({
        open: openResult.count || 0,
        today: todayResult.count || 0,
        waiting: waitingResult.count || 0,
        wonMonth: monthRows.filter((row) => row.status === 'won').length,
        lostMonth: monthRows.filter((row) => row.status === 'lost').length,
        conversion: won + lost === 0 ? 0 : Math.round((won / (won + lost)) * 100),
        recent: (recentResult.data || []) as unknown as LeadRow[]
      })
    }
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])

  const stats = useMemo(() => [
    { label: t('openLeads'), value: String(data.open), meta: t('summary') },
    { label: t('leadsToday'), value: String(data.today), meta: t('today') },
    { label: t('waitingLeads'), value: String(data.waiting), meta: t('status') },
    { label: t('conversion'), value: `${data.conversion}%`, meta: t('decidedLeads') }
  ], [data, t])

  if (loading) return <LoadingBlock />

  return (
    <div className="dashboard-grid">
      <section className="welcome-card">
        <div>
          <span className="eyebrow">SG Flow R1</span>
          <h2>{t('welcome')}</h2>
          <p>{t('dashboardIntro')}</p>
        </div>
        <div className="flower-orbit" aria-hidden="true"><span>✿</span><span>✦</span><span>●</span></div>
      </section>

      {error && <div className="dashboard-notice"><InlineNotice type="error">{error}</InlineNotice></div>}

      <section className="stats-grid">
        {stats.map((stat) => <article className="stat-card" key={stat.label}><span>{stat.label}</span><strong>{stat.value}</strong><small>{stat.meta}</small></article>)}
      </section>

      <section className="panel upcoming-panel dashboard-main-panel">
        <div className="panel-heading"><div><span className="eyebrow">CRM</span><h3>{t('recentLeads')}</h3></div><button className="text-button" onClick={() => void load()}>{t('refresh')}</button></div>
        {data.recent.length === 0 ? <EmptyState title={t('leadsEmpty')} description={t('noRecentLeads')} /> : (
          <div className="timeline-list">
            {data.recent.map((lead) => (
              <button className="timeline-item timeline-button" key={lead.id} type="button" onClick={() => onOpenLead?.(lead.id)}>
                <time>{formatDate(lead.created_at, language)}</time>
                <div><strong>{lead.clients ? clientDisplayName(lead.clients) : lead.contact_label || '—'}</strong><span>{serviceName(lead.services, language)} · {lead.responsible?.full_name || lead.responsible?.email || '—'}</span></div>
                <StatusBadge tone={leadStatusTone(lead.status)}>{leadStatusLabel(lead.status, language)}</StatusBadge>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="panel activity-panel">
        <div className="panel-heading"><div><span className="eyebrow">{t('summary')}</span><h3>{t('decidedLeads')}</h3></div></div>
        <div className="decision-summary">
          <div><span>{t('wonThisMonth')}</span><strong>{data.wonMonth}</strong></div>
          <div><span>{t('lostThisMonth')}</span><strong>{data.lostMonth}</strong></div>
          <div><span>{t('conversion')}</span><strong>{data.conversion}%</strong></div>
        </div>
      </section>
    </div>
  )
}
